import { useCallback, useEffect, useRef, useState } from 'react';
import { Paragraph35aKarte } from '../../../../../components/buchhaltung/Paragraph35a';
import { AufstellungKarte, JahrWahl, KeineSteuerberatung, NichtAnlageVKarte, UngeklaertKarte, WegSchluesselKarte } from '../../../../../components/buchhaltung/Steuerkarten';
import { standardJahr, useAufstellung, useSteuerDaten } from '../../../../../lib/buchhaltung/useSteuerDaten';
import { ArrowsClockwise, FileCsv, UploadSimple, WarningCircle } from '@phosphor-icons/react';
import { BestaetigenDialog, Chip, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, ToastPill, useToastPill } from '../../../../../components/immobilien/ui';
import { importiereJournal, ladeImportStand, type ImportStand } from '../../../../../lib/buchhaltung/api';
import { leseJournalText, parseJournal, type JournalErgebnis } from '../../../../../lib/buchhaltung/immowareJournal';
import { useObjektSeite } from '../ObjektSeitenKontext';

const datum = (iso: string | null) => (iso ? new Date(iso.length === 10 ? iso + 'T12:00:00' : iso).toLocaleDateString('de-DE') : '–');
const zeitpunkt = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * Panel „Finanzen“ (Steuerpaket S1): Immoware-Buchungsjournal als CSV einlesen. Ein neuer Import
 * ersetzt den bisherigen Stand des Objekts. Die Buchungen sind Grundlage der Anlage-V-Aufstellung.
 */
export function FinanzenPanel() {
  const { daten, verwaltungsart } = useObjektSeite();
  const objektId = daten.objekt.id;
  const weg = verwaltungsart === 'WEG';
  const steuer = useSteuerDaten(objektId, weg);
  const [jahrWahl, setJahrWahl] = useState<number | null>(null);
  const jahr = jahrWahl ?? standardJahr(steuer.jahre);
  const { ergebnis, verteilung } = useAufstellung(steuer, jahr);
  const [stand, setStand] = useState<ImportStand | null | undefined>(undefined);
  const [datei, setDatei] = useState<{ name: string; ergebnis: JournalErgebnis } | null>(null);
  const [fragen, setFragen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  const eingabe = useRef<HTMLInputElement>(null);

  const lade = useCallback(async () => {
    try { setStand(await ladeImportStand(objektId)); } catch (e) { setStand(null); zeige(`Stand nicht geladen: ${(e as Error).message}`, true); }
  }, [objektId, zeige]);
  useEffect(() => { void lade(); }, [lade]);

  const waehle = async (f: File | undefined) => {
    if (!f) return;
    try { setDatei({ name: f.name, ergebnis: parseJournal(leseJournalText(await f.arrayBuffer())) }); }
    catch (e) { zeige(`Datei nicht lesbar: ${(e as Error).message}`, true); }
    if (eingabe.current) eingabe.current.value = '';
  };

  const importiere = async () => {
    if (!datei) return;
    setLaeuft(true);
    try {
      await importiereJournal(objektId, datei.name, datei.ergebnis.zeilen);
      zeige(`${datei.ergebnis.zeilen.length.toLocaleString('de-DE')} Buchungen importiert`);
      setDatei(null); setFragen(false);
      await lade();
      steuer.neuLaden();
    } catch (e) { zeige(`Import fehlgeschlagen: ${(e as Error).message}`, true); }
    finally { setLaeuft(false); }
  };

  const erg = datei?.ergebnis;
  const importierbar = !!erg && erg.zeilen.length > 0;

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <InnenKarte>
        <KartenTitel icon={FileCsv} rechts={stand ? `importiert ${zeitpunkt(stand.importiert_am)}` : undefined}>Buchhaltung aus Immoware</KartenTitel>
        {stand === undefined ? <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Lädt …</span>
          : stand ? (
            <KpiReihe minBreite={150}>
              <KpiTile label="Buchungen" wert={stand.zeilen.toLocaleString('de-DE')} sub={stand.dateiname ?? undefined} />
              <KpiTile label="Zahlungen von" wert={datum(stand.wertstellung_von)} />
              <KpiTile label="bis" wert={datum(stand.wertstellung_bis)} />
            </KpiReihe>
          ) : <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Für dieses Objekt ist noch kein Buchungsjournal importiert.</span>}
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
          In Immoware das Buchungsjournal des Objekts als CSV exportieren, am besten nach Zahlungsdatum ab dem Vorjahr bis heute.
          Ein neuer Import ersetzt den bisherigen Stand dieses Objekts. Eigentümer sehen die Buchungen nicht.
        </span>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <input ref={eingabe} type="file" accept=".csv,text/csv" hidden aria-label="Immoware-Journal auswählen" onChange={(e) => void waehle(e.target.files?.[0])} />
          <ImmoButton art="glas" icon={UploadSimple} onClick={() => eingabe.current?.click()} disabled={laeuft}>CSV auswählen</ImmoButton>
        </div>
      </InnenKarte>

      {erg && datei && (
        <InnenKarte>
          <KartenTitel icon={FileCsv} rechts={datei.name}>Vorschau</KartenTitel>
          {erg.zeilen.length > 0 && (
            <KpiReihe minBreite={150}>
              <KpiTile label="Buchungen" wert={erg.zeilen.length.toLocaleString('de-DE')} />
              <KpiTile label="Zahlungen von" wert={datum(erg.von)} />
              <KpiTile label="bis" wert={datum(erg.bis)} />
            </KpiReihe>
          )}
          {erg.bankkonten.length > 0 && (
            <div className="flex flex-wrap items-center" style={{ gap: 6, fontSize: 13 }}>
              <span style={{ color: 'var(--ink3)' }}>Bankkonten in der Datei – passt das zum Objekt?</span>
              {erg.bankkonten.map((b) => <Chip key={b}>{b}</Chip>)}
            </div>
          )}
          {erg.fehler.length > 0 && (
            <div className="flex flex-col" style={{ gap: 4, fontSize: 13, color: 'var(--bad)' }}>
              <span className="flex items-center" style={{ gap: 6, fontWeight: 700 }}><WarningCircle weight="fill" size={16} />
                {erg.zeilen.length ? `${erg.fehler.length} Zeilen werden übersprungen` : 'Nicht importierbar'}</span>
              {erg.fehler.slice(0, 8).map((f) => <span key={f}>{f}</span>)}
              {erg.fehler.length > 8 && <span>… und {erg.fehler.length - 8} weitere</span>}
            </div>
          )}
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <ImmoButton art="akzent" icon={ArrowsClockwise} disabled={!importierbar || laeuft} onClick={() => (stand ? setFragen(true) : void importiere())}>
              {stand ? 'Importieren und ersetzen' : 'Importieren'}
            </ImmoButton>
            <ImmoButton art="glas" onClick={() => setDatei(null)} disabled={laeuft}>Verwerfen</ImmoButton>
          </div>
        </InnenKarte>
      )}

      {ergebnis && jahr != null && (
        <>
          <div className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
            <JahrWahl jahre={steuer.jahre} wert={jahr} onChange={setJahrWahl} />
            <KeineSteuerberatung />
          </div>
          <AufstellungKarte titel={weg ? `Ausgaben der WEG ${jahr}` : `Anlage V ${jahr} – ganzes Objekt`} rechts="nach Zahlungsdatum"
            posten={ergebnis.posten} nurKosten={weg} />
          <UngeklaertKarte objektId={objektId} ergebnis={ergebnis} klaerungen={steuer.klaerungen} onGeaendert={steuer.neuLaden} zeige={zeige} />
          {weg && verteilung && (
            <WegSchluesselKarte objektId={objektId} ergebnis={ergebnis} einheiten={steuer.einheiten} schluessel={steuer.schluessel}
              verteilung={verteilung} onGeaendert={steuer.neuLaden} zeige={zeige} />
          )}
          <NichtAnlageVKarte ergebnis={ergebnis} />
        </>
      )}
      {weg && !steuer.laedt && (
        <>
          {!ergebnis && <JahrWahl jahre={[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2]} wert={jahr ?? new Date().getFullYear() - 1} onChange={setJahrWahl} />}
          <Paragraph35aKarte objektId={objektId} jahr={jahr ?? new Date().getFullYear() - 1} zeige={zeige} />
        </>
      )}
      {steuer.fehler && <span style={{ fontSize: 13, color: 'var(--bad)' }}>Aufstellung nicht geladen: {steuer.fehler}</span>}

      {fragen && stand && erg && (
        <BestaetigenDialog
          titel="Buchhaltung ersetzen?"
          text={`Die bisherigen ${stand.zeilen.toLocaleString('de-DE')} Buchungen dieses Objekts werden durch ${erg.zeilen.length.toLocaleString('de-DE')} Buchungen aus „${datei!.name}“ ersetzt.`}
          bestaetigenLabel="Ersetzen" icon={ArrowsClockwise} gefahr={false} laeuft={laeuft}
          onBestaetigen={() => void importiere()} onAbbrechen={() => setFragen(false)}
        />
      )}
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
    </div>
  );
}
